/**
 * Durations, formatted the three ways this app needs them.
 *
 * One module because a timesheet that says "1.5" next to a bar that says
 * "1:30:00" next to an invoice that says "1h 30m" reads as three different
 * numbers, and somebody will add them up wrong.
 */

/** The ticking clock: 1:04:09. Hours are not padded — 0:59 is not 00:59. */
export function clockFormat(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n) => String(n).padStart(2, "0")
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

/** For reading: 1h 30m. Minutes are dropped once there are none. */
export function humanDuration(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0))
  if (total < 60) return `${total}s`
  const h = Math.floor(total / 3600)
  const m = Math.round((total % 3600) / 60)
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

/**
 * For a report column: "6h 30m", with the minutes padded and an em dash for
 * nothing.
 *
 * Padded because a column of `6h 5m` beside `6h 30m` does not line up, and a
 * report is read down the column. The dash rather than "0h 0m", which reads as
 * a measurement when it is an absence.
 */
export function hoursLabel(seconds) {
  const total = Math.round(Number(seconds || 0) / 60)
  const h = Math.floor(total / 60)
  const m = total % 60
  if (!h && !m) return "—"
  return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`
}

/** For arithmetic and invoices: 1.50. Two decimals, the unit hours are sold in. */
export function decimalHours(seconds) {
  return (Math.round((Number(seconds) || 0) / 36) / 100).toFixed(2)
}

/**
 * Seconds from what somebody types in a duration box. Null when unreadable.
 *
 * Accepts, in order: `1:30:15` and `1:30` (clock), `1h30m15s` and any subset of
 * it, and a bare number, which means hours because that is what people type on
 * a timesheet.
 *
 * Seconds are here because the timesheet PRINTS them — a six-second entry reads
 * "6s", and a field that will not accept back the string it just showed you is a
 * field that cannot correct its own display. They are not here because anybody
 * bills by the second.
 *
 * One ambiguity is left deliberately: `1:30` is an hour and a half, not ninety
 * seconds, even though the running clock renders 90 seconds as "1:30". The
 * two-part clock form has meant hours and minutes on every timesheet anyone has
 * used, the field says so, and quietly changing it would silently divide
 * somebody's week by sixty. Use `90s` to mean ninety seconds, or `0:01:30`.
 */
export function parseDuration(input) {
  const raw = String(input ?? "").trim().toLowerCase()
  if (!raw) return null

  // 1:30:15 — hours, minutes, seconds. 1:30 — hours and minutes.
  const clock = raw.match(/^(\d+):([0-5]?\d)(?::([0-5]?\d))?$/)
  if (clock) return Number(clock[1]) * 3600 + Number(clock[2]) * 60 + Number(clock[3] || 0)

  // 90m, 2h, 1h30m, 45s, 1h30m15s
  const parts = raw.match(/^(?:(\d+(?:\.\d+)?)h)?\s*(?:(\d+(?:\.\d+)?)m)?\s*(?:(\d+(?:\.\d+)?)s)?$/)
  if (parts && (parts[1] || parts[2] || parts[3])) {
    return Math.round(Number(parts[1] || 0) * 3600 + Number(parts[2] || 0) * 60 + Number(parts[3] || 0))
  }

  // A bare number is hours, because that is what people type on a timesheet.
  const decimal = Number(raw)
  if (Number.isFinite(decimal) && decimal > 0) return Math.round(decimal * 3600)

  return null
}
